import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import ConfirmPopups from './ConfirmPopup';
import { useNavigate } from 'react-router-dom';

interface ItemTask {
    id: number;
    jobQuoteItemId: number;
    installPickupDateFormatted: string;
    scheduleDate: string;
    scheduleDateFormatted: string;
    typeId: number;
    type: string;
    jobId: number;
    siteAddress: string;
    customer: string;
    customerEmail: string;
    itemId: number;
    item: string;
    unitNumber: string;
    complete: boolean
}

const ItemTaskData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [itemTasks, setItemTasks] = React.useState<ItemTask[]>([]);
    const [itemIndex, setItemIndex] = React.useState(-1);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("ViewItemTasks");
        if (hasPermission) {
            getItemTasks();
        } else {
            navigate("/accessdenied");
        }
    }

    const getItemTasks = async () => {
        const apiUrl = "GetItemTasks";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting item tasks!");
        } else {
            setItemTasks(result.data);
            setLoading(false);
        } 
    }

    const onTaskDateChange = (e: any, itemIndex: number) => {
        var items = [...itemTasks];
        var item = items[itemIndex];
        item.scheduleDate = e.target.value;
        setItemTasks(items);
        updateItem(item.id, e.target.value);
    }

    const updateItem = async (id: number, scheduleTaskDate: string) => {
        const apiUrl = "UpdateItemTask";
        var updateTaskItem = {
            id: id,
            scheduleTaskDate: scheduleTaskDate
        };
        var jsonTaskItem = JSON.stringify(updateTaskItem);

        var result = await PostQuery(apiUrl, jsonTaskItem);
        if (result.error) {
            toast.error("Error saving item tasks!");
        } else {
            toast.success("Task Updated Successfully");
        } 
    }

    const onUnitNoChange = (e: any, itemIndex: number) => {
        var items = [...itemTasks];
        var item = items[itemIndex];
        item.unitNumber = e.target.value;
        setItemTasks(items);
        saveItem(item.id, item.complete, e.target.value);
    }

    const onCellCheckboxChange = (e: any, itemIndex: number) => {
        var items = [...itemTasks];
        var item = items[itemIndex];
        item.complete = e.target.checked;
        setItemTasks(items);
        saveItem(item.id, e.target.checked, item.unitNumber);
    }

    const saveItem = async (id: number, complete: boolean, unitNo: string) => {
        const apiUrl = "SaveItemTask";
        var saveItem = {
            id: id,
            complete: complete,
            unitNumber: unitNo
        };
        var jsonItem = JSON.stringify(saveItem);

        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving item tasks!");
        } else {
            toast.success("Task Saved Successfully");
        }
    }

    //DELETE ITEM
    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        setItemIndex(itemIndex);
        setShowConfirmDelete(true);
    }

    const hideConfirmDelete = () => {
        setItemIndex(-1);
        setShowConfirmDelete(false);
    }

    const saveDelete = () => {
        saveDeleteTask();
    }

    const saveDeleteTask = async () => {
        const apiUrl = "DeleteItemTask";
        var items = [...itemTasks];
        var item = items[itemIndex];
        var deleteItem = {
            id: item.id
        };

        var jsonItem = JSON.stringify(deleteItem);

        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error deleting item task!");
        } else {
            items.splice(itemIndex, 1);
            setItemTasks(items);
            setItemIndex(-1);
            setShowConfirmDelete(false);
            toast.success("Task Deleted Successfully");
        }     
    }
    //END DELETE ITEM

    const minDate = DateHelpers.addDaysToCurrentDate(-7);
    const confirmDelete = <ConfirmPopups heading="Delete Task" text="Are you sure you want to delete this task?" showConfirmModal={showConfirmDelete} hideConfirmModal={hideConfirmDelete} saveConfirmModal={saveDelete} />

    const renderDetails = (
        <div>
            <div>
                {confirmDelete}
            </div>
            <div className="overflowAuto marginTop10">
                <table className="table tableColours">
                    <thead>
                        <tr>
                            <th className="hidden">Id</th>
                            <th>
                                <div className="tooltipitem tooltipitem__right">Schedule Date
                                    <span className="tooltipitemtext tooltipitemtext__right">Date that the Task is scheduled for</span>
                                </div>
                            </th>
                            <th>Type</th>
                            <th>
                                <div className="tooltipitem tooltipitem__right">Install/Pickup Date
                                    <span className="tooltipitemtext tooltipitemtext__right">Date that the Item was Installed or Picked up</span>
                                </div>
                            </th>
                            <th>Site Address</th>
                            <th>Customer</th>
                            <th>Customer Email</th>
                            <th>Item</th>
                            <th>Unit #</th>
                            <th className="textaligncenter">Complete</th>
                            <th></th>
                        </tr>
                    </thead>
                    {itemTasks.map((item, index) =>
                        <tbody key={index}>
                            <tr>
                                <td className="hidden">{item.id}</td>
                                <td className="table__text--align">
                                    <input type='date' min={minDate} className="form-control allocate__dateSize" value={item.scheduleDate} onChange={(e) => onTaskDateChange(e, index)} />
                                </td>
                                <td className="table__text--align">{item.type}</td>
                                <td className="table__text--align">{item.installPickupDateFormatted}</td>
                                <td className="table__text--align">{item.siteAddress}</td>
                                <td className="table__text--align">{item.customer}</td>
                                <td className="table__text--align">{item.customerEmail}</td>
                                <td className="table__text--align">{item.typeId === 4 ? "" : item.item}</td>
                                <td className="table__text--align">
                                    <input type='text' id="unitNumber" name="unitNumber" className={item.typeId > 2 ? "hidden" : "form-control allocate__size100"} value={item.unitNumber} onChange={(e) => onUnitNoChange(e, index)}  />
                                </td>
                                <td className="table__text--align">
                                    <input type='checkbox' id="complete" name="complete" className="alignIconCenter" checked={item.complete} onChange={(e) => onCellCheckboxChange(e, index)} />
                                </td>
                                <td>
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                            <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default ItemTaskData;
